import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { v4 as uuidv4 } from "uuid";
import { gapi } from "gapi-script";

import "./upload-image.css";

import ImageThumb from "./ImageThumb";
import { UploadPreview } from "./UploadPreview";
import { deleteClientJournalImages, deleteClientProfileImage, getSASToken, uploadClientJournalImages, uploadClientProfileImage } from "./AzureStorage.service";
import API, { getTherapistAzureContainer, set, setGoogleUploadForClientProfile, setGoogleUploadForJournalImage, setJournalImage } from "../../actions";
import { createFolder, deleteClientProfileData, deleteJournalData, findFolder, getFilesInFolder, uploadFilesToFolder } from "./googledrivefunc";
import { BrowserView, MobileView } from 'react-device-detect';

import azureStorageEnvVar from "../../constants/AzureStorageEnvironmentVariable";
import { DialogCaptureImage } from "./DialogPopups";
import { useDispatch } from "react-redux";
import update from "immutability-helper";

// prop for multiple:true,maxFiles:10 / multiple:false
export const UploadFile = ({ multi, maxFiles = 1, client, journal, therapist, uploadInDrive }) => {
    const dispatch = useDispatch();

    const [storageSize, setStorageSize] = useState(0);

    const [files, setFiles] = useState([]);
    const [previewFiles, setPreviewFiles] = useState([]);

    const [error, setError] = useState({ errorStatus: false, errorMessage: "" });

    const [parentFolderId, setParentFolderId] = useState(null);
    const [childFolderId, setChildFolderId] = useState(null);

    const [dragging, setDragging] = useState(false);
    const [editMode, setEditMode] = useState({});
    const fileInputRef = useRef(null);

    const [openCaptureDialog, setOpenCaptureDialog] = useState(false);

    const updateTherapistStorage = async () => {
        const updateTherapist = await API.getTherapist();
        const updateStorage = updateTherapist.storageUsedInBytes;
        dispatch(
            set({
                therapist: update(therapist, {
                    storageUsedInBytes: { $set: updateStorage },
                })
            })
        );
        setStorageSize(updateStorage);
    };

    const handleDivClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setPreviewFiles(val => [
                ...val,
                Object.assign(file, {
                    id: uuidv4(),
                    imageUrl: URL.createObjectURL(file)
                })
            ]);
        }
    };

    const clearFiles = useCallback(async (file) => {
        const updatedFiles = files.filter(fileData => fileData.imageName !== file.imageName);

        if (multi) {
            const onlyDbFiles = updatedFiles.filter(updateFile => !updateFile.isGoogleImage);
            await setJournalImage(onlyDbFiles.map(file => file.imageName).join(','), client?.id, journal.id);
        }
        setFiles(updatedFiles);

        await updateTherapistStorage()
    }, [client.id, journal, files])

    const handleChangeImage = data => {
        data.id = uuidv4();
        setEditMode(data);
        setPreviewFiles([data]);
    };

    const handleDeleteImagePreview = id => {
        setPreviewFiles(val => val.filter(file => file.id !== id));
    };

    const handleCancelPreview = () => {
        setPreviewFiles([]);
        setEditMode({});
        acceptedFiles.splice(0, acceptedFiles.length);
    };

    const handleError = useCallback(
        errorType => {
            setTimeout(() => {
                handleCloseError()
            }, 3000);
            switch (errorType) {
                case "maxFile":
                    // `Maximum of ${maxFiles} images allowed`
                    return setError({ errorStatus: true, errorMessage: `Maksimalt ${maxFiles} billeder tilladt` });

                case "maxFileEdit":
                    // "Only 1 image allowed for editing"
                    return setError({ errorStatus: true, errorMessage: "Kun 1 billede tilladt til redigering" });

                case "maxFileSize":
                    // "Image size must be within 4 MB"
                    return setError({ errorStatus: true, errorMessage: "Billedstørrelse skal være inden for 4 MB" });

                case "storage":
                    // "Your storage is full"
                    return setError({ errorStatus: true, errorMessage: "Din lagerplads er fuld" });

                case "minFileRequired":
                    // "At least 1 image is required"
                    return setError({ errorStatus: true, errorMessage: "Mindst 1 billede er påkrævet" });

                case "fileRejections":
                    // "Your storage is full"
                    return setError({ errorStatus: true, errorMessage: "Filen skal være JPEG, JPG, GIF, PNG filtyper." });

                default:
                    return setError({ errorStatus: false, errorMessage: "" });
            }
        },
        [maxFiles]
    );

    const handleCloseError = () => {
        setError({ errorStatus: false, errorMessage: "" });
    };

    const handleUpload = useCallback(
        async imageFile => {
            if (previewFiles[0].isUploaded) {
                // then do nothing
                acceptedFiles.splice(0, acceptedFiles.length);
                return handleCancelPreview();
            }

            if (uploadInDrive) {
                await handleUploadGoogle(imageFile);
            } else {
                const {
                    AZURE_STORAGE_URL: azureUrl,
                    RESIZED_IMG_PREFIX: resizePrefix,
                    CLIENT_PREFIX,
                } = azureStorageEnvVar;

                const getTherapistContainer = await getTherapistAzureContainer(therapist?.id);
                const SASToken = await getSASToken(getTherapistContainer);

                if (multi) {
                    // delete one UpdateButton Click
                    if (Object.keys(editMode).length > 0) {
                        if (editMode.isGoogleImage) {
                            const newUpdatedFiles = files.filter(file => file.imageName !== editMode.imageName).filter(file => file.isGoogleImage);
                            await deleteJournalData(editMode, newUpdatedFiles, journal.id)
                        } else {
                            await deleteClientJournalImages(therapist?.id, client?.id, [editMode.imageName]);
                        }
                    }

                    // upload new one
                    const imagesName = await uploadClientJournalImages(therapist?.id, client?.id, imageFile);
                    const updatedFiles = editMode ? files.filter(file => file.imageName !== editMode.imageName) : [];
                    imagesName.map(imageName => {
                        const imageUrl = `${azureUrl}/${getTherapistContainer}/${CLIENT_PREFIX}_${client?.id}/${resizePrefix}_${imageName}?${SASToken}`;
                        updatedFiles.push({ imageUrl: imageUrl, imageName: imageName, clientId: client?.id, therapistId: therapist?.id, isUploaded: true });
                    })
                    const localFiles = updatedFiles.filter(file => !file.isGoogleImage)
                    await setJournalImage(localFiles.map(file => file.imageName)?.join(','), client?.id, journal.id);
                    setFiles(updatedFiles);
                }
                else {
                    // delete previous and
                    if (Object.keys(editMode).length > 0) {
                        if (editMode.isGoogleImage) {
                            await deleteClientProfileData(editMode);
                        } else {
                            await deleteClientProfileImage(therapist?.id, client?.id, editMode.imageName);
                        }
                    }
                    // upload new one

                    const response = await uploadClientProfileImage(therapist?.id, client?.id, imageFile[0]);
                    const imageUrl = `${azureUrl}/${getTherapistContainer}/${CLIENT_PREFIX}_${client?.id}/${resizePrefix}_${response}?${SASToken}`;
                    setFiles([{ imageUrl, imageName: response, clientId: client?.id, therapistId: therapist?.id, isUploaded: true }]);
                }
            }
            acceptedFiles.splice(0, acceptedFiles.length);
            await updateTherapistStorage()

            return handleCancelPreview();
        },
        [client, editMode, files, journal, multi, previewFiles]
    );

    // Drop Data store in API with Check Size individual and Totle storage
    const onDrop = useCallback(
        async acceptedFiles => {
            const sumAcceptedFilesSize = acceptedFiles.reduce(
                (accumulator, currentValue) => accumulator + currentValue.size,
                0,
            );
            if (Number(storageSize) + sumAcceptedFilesSize < 1073741824) {
                acceptedFiles.map(file => {
                    if (file.size > 4194304) {
                        return handleError("maxFileSize");
                    } else {
                        return setPreviewFiles(val => [
                            ...val,
                            Object.assign(file, {
                                id: uuidv4(),
                                imageUrl: URL.createObjectURL(file)
                            })
                        ]);
                    }
                });
            } else {
                handleError("storage");
            }
        },
        [handleError, storageSize]
    );

    // Drop Zone use for drop data-get
    const { getRootProps, getInputProps, acceptedFiles } = useDropzone({
        onDrop, multiple: true, noClick: true, accept: {
            'image/jpeg': [],
            'image/png': [],
            'image/gif': [],
            'image/jpg': []
        }
    });

    console.log("new storageSize:",storageSize, therapist.storageUsedInBytes, acceptedFiles);

    const captureCameraImage = (blob) => {
        const filename = `${client?.nameFirst} ${client?.nameLast}`;
        const sanitizedFilename = `${filename.replace(/[^a-z0-9]/gi, "_").toLowerCase()}-${Date.now()}`;
        const file = new File([blob], sanitizedFilename, { type: blob.type });

        setPreviewFiles(val => [
            ...val,
            Object.assign(file, {
                id: uuidv4(),
                imageUrl: URL.createObjectURL(file)
            })
        ])
    }

    const handleUploadGoogle = async (newFiles) => {
        const parentFolderName = 'HandsOn Booking';
        const childFolderName = `${client?.nameFirst} ${client?.nameLast} - (${client?.email})`;

        try {
            // Find or create the parent folder
            let parentId = parentFolderId || await findFolder(parentFolderName) || await createFolder(parentFolderName);
            setParentFolderId(parentId);

            // Find or create the child folder within the parent folder
            let childId = childFolderId || await findFolder(childFolderName, parentId) || await createFolder(childFolderName, parentId);
            setChildFolderId(childId);

            // Upload new files to the child folder
            const newAddFiles = await uploadFilesToFolder(newFiles, childId);

            if (multi) {
                if (Object.keys(editMode).length > 0) {
                    // check isUploaded on Google or Azure
                    if (editMode.isGoogleImage) {
                        // isUploaded on Google then delete on google and upload on google
                        const newUpdatedFiles = files.filter(file => file.imageName !== editMode.imageName);
                        const newUpdatedGoogleFiles = [
                            ...newUpdatedFiles.filter(file => file.isGoogleImage),
                            ...newAddFiles.map(file => ({
                                imageUrl: file.imageUrl,
                                imageName: file.name,
                                clientId: client.id,
                                therapistId: therapist.id,
                                isUploaded: true,
                                isGoogleImage: file.id,
                            }))];
                        await deleteJournalData(editMode, newUpdatedGoogleFiles, journal.id);

                        setFiles([...newUpdatedFiles,
                        ...newAddFiles.map(file => ({
                            imageUrl: file.imageUrl,
                            imageName: file.name,
                            clientId: client.id,
                            therapistId: therapist.id,
                            isUploaded: true,
                            isGoogleImage: file.id,
                        }))]);
                    } else {
                        // isUploaded on Azure then delete on Azure and upload on google
                        const newUpdatedFiles = files.filter(file => file.imageName !== editMode.imageName);
                        const newUpdatedAzureFiles = newUpdatedFiles.filter(file => !file.isGoogleImage);
                        const deleteResponse = await deleteClientJournalImages(therapist.id, client.id, [editMode.imageName]);
                        if (deleteResponse) {
                            await setJournalImage(newUpdatedAzureFiles.map(file => file.imageName)?.join(','), client?.id, journal.id);

                            const remainGoogleFiles = [
                                ...newUpdatedFiles.filter(file => file.isGoogleImage),
                                ...newAddFiles.map(file => ({
                                    imageUrl: file.imageUrl,
                                    imageName: file.name,
                                    clientId: client.id,
                                    therapistId: therapist.id,
                                    isUploaded: true,
                                    isGoogleImage: file.id,
                                }))];

                            await setGoogleUploadForJournalImage(remainGoogleFiles.map(fileData => fileData.imageName).join(','), client.id, journal.id);
                        }

                        setFiles([...newUpdatedFiles,
                        ...newAddFiles.map(file => ({
                            imageUrl: file.imageUrl,
                            imageName: file.name,
                            clientId: client.id,
                            therapistId: therapist.id,
                            isUploaded: true,
                            isGoogleImage: file.id,
                        }))]);
                    }

                } else {
                    const googleFiles = files.filter(file => file.isGoogleImage);
                    const localFiles = files.filter(file => !file.isGoogleImage);

                    const updatedGoogleFiles = [
                        ...googleFiles,
                        ...newAddFiles.map(file => ({
                            imageUrl: file.imageUrl,
                            imageName: file.name,
                            clientId: client.id,
                            therapistId: therapist.id,
                            isUploaded: true,
                            isGoogleImage: file.id,
                        }))
                    ];
                    await setGoogleUploadForJournalImage(updatedGoogleFiles.map(file => file.imageName).join(','), client.id, journal.id);
                    setFiles([...localFiles, ...updatedGoogleFiles]);
                }
            } else {

                if (Object.keys(editMode).length > 0) {
                    // check isUploaded on Google or Azure
                    if (editMode.isGoogleImage) {
                        // isUploaded on Google then delete on google and upload on google
                        await deleteClientProfileData(editMode);
                    } else {
                        // isUploaded on Azure then delete on Azure and upload on google
                        await deleteClientProfileImage(therapist.id, client.id, [editMode.imageName]);
                    }
                }

                const [uploadedFile] = newAddFiles;
                await setGoogleUploadForClientProfile(uploadedFile.name, client.id);
                setFiles([{
                    imageUrl: uploadedFile.imageUrl,
                    imageName: uploadedFile.name,
                    clientId: client.id,
                    therapistId: therapist.id,
                    isUploaded: true,
                    isGoogleImage: uploadedFile.id,
                }]);
            }
        } catch (error) {
            console.error('An error occurred during folder creation or file upload:', error);
        }
    };

    const initClient = async () => {
        try {
            let parentId = parentFolderId;
            let childId = childFolderId;

            parentId = await findFolder('HandsOn Booking');
            setParentFolderId(parentId);

            childId = await findFolder(`${client?.nameFirst} ${client?.nameLast} - (${client?.email})`, parentId);
            setChildFolderId(childId);

            if (client?.profileImageOnGoogle && !multi) {
                let profileImage;
                try {
                    const filesRetrieveDrive = await getFilesInFolder(childId, client.profileImageOnGoogle);
                    profileImage = {
                        imageUrl: filesRetrieveDrive[0].imageUrl,
                        imageName: client.profileImageOnGoogle,
                        clientId: client.id,
                        therapistId: therapist.id,
                        isUploaded: true,
                        isGoogleImage: filesRetrieveDrive[0].id,
                    }
                } catch (error) {
                    console.log("not found", error);
                    profileImage = {
                        imageUrl: "",
                        imageName: client.profileImageOnGoogle,
                        clientId: client.id,
                        therapistId: therapist.id,
                        isUploaded: true,
                        isGoogleImage: true,
                    }
                }

                setFiles([profileImage])
            } else if (journal?.imagesNameOnGoogle) {
                const imageJournalArray = journal.imagesNameOnGoogle.split(',');
                const updatedFilesPromises = imageJournalArray.map(async imageFile => {
                    try {
                        const filesRetrieveDrive = await getFilesInFolder(childId, imageFile);
                        return {
                            imageUrl: filesRetrieveDrive[0].imageUrl,
                            imageName: imageFile,
                            clientId: client.id,
                            therapistId: therapist.id,
                            isUploaded: true,
                            isGoogleImage: filesRetrieveDrive[0].id,
                        };
                    } catch (error) {
                        console.log("not found", error);
                        return {
                            imageUrl: '',
                            imageName: imageFile,
                            clientId: client.id,
                            therapistId: therapist.id,
                            isUploaded: true,
                            isGoogleImage: true,
                        };
                    }
                });

                const updatedFiles = await Promise.all(updatedFilesPromises);
                setFiles(prevFiles => [...prevFiles, ...updatedFiles]);
            }
        } catch (error) {
            console.error('An error occurred:', error);
        }
    };

    // For drag then Drop area highlight
    useEffect(() => {
        document.addEventListener("dragenter", function (e) {
            e.preventDefault();
            setDragging(true);
        });
        document.addEventListener("drop", function () {
            setDragging(false);
        });
        document.addEventListener("dragleave", function (event) {
            if (event.relatedTarget == null || event.relatedTarget.nodeName === "HTML") {
                setDragging(false);
            }
        });
        return () => {
            document.removeEventListener("dragenter", function () {
                setDragging(false);
            });
            document.removeEventListener("drop", function () {
                setDragging(false);
            });
            document.removeEventListener("dragleave", function () {
                setDragging(false);
            });
        };
    }, []);

    const getFile = async () => {
        const {
            AZURE_STORAGE_URL: azureUrl,
            RESIZED_IMG_PREFIX: resizePrefix,
            CLIENT_PREFIX,
        } = azureStorageEnvVar;

        const therapistId = therapist?.id;
        const clientId = client?.id;

        const therapistContainer = await getTherapistAzureContainer(therapistId);
        const sasToken = await getSASToken(therapistContainer);

        setStorageSize(therapist?.storageUsedInBytes);

        if (client?.profileImage && !multi) {
            const imageUrl = `${azureUrl}/${therapistContainer}/${CLIENT_PREFIX}_${clientId}/${resizePrefix}_${client.profileImage}?${sasToken}`;
            setFiles([{
                imageUrl,
                imageName: client.profileImage,
                clientId,
                therapistId,
                isUploaded: true
            }]);
        } else if (journal?.imagesName) {
            const imageJournalArray = journal.imagesName.split(',');
            const updatedFiles = imageJournalArray.map(imageName => {
                const imageUrl = `${azureUrl}/${therapistContainer}/${CLIENT_PREFIX}_${clientId}/${resizePrefix}_${imageName}?${sasToken}`;
                return {
                    imageUrl,
                    imageName,
                    clientId,
                    therapistId,
                    isUploaded: true,
                }
            });

            setFiles(prevFiles => [...prevFiles, ...updatedFiles]);
        } else {
            setFiles([]);
        }
    };

    const checkGoogleFiles = async () => {
        const authInstance = await gapi.auth2.getAuthInstance().isSignedIn.get();
        if (!authInstance) {
            if (journal?.imagesNameOnGoogle?.length) {
                const imageArrayOfGoogle = journal?.imagesNameOnGoogle?.split(',');
                if (imageArrayOfGoogle.length > 0) {
                    // setError({ errorStatus: true, errorMessage: "Image on google please login" });
                }
            }
            // let imageArrayOfGoogle = [];
            // let imageArrayOfAzure = [];
            // if (journal?.imagesNameOnGoogle?.length) {
            //     imageArrayOfGoogle = journal?.imagesNameOnGoogle?.split(',');
            // } if (journal?.imagesName?.length) {
            //     imageArrayOfAzure = journal?.imagesName?.split(',');
            // }
            // console.log("imageLength", imageArrayOfGoogle.length + imageArrayOfAzure.length);
            // console.log("imageOfGoogleLength", imageArrayOfGoogle.length);
            // console.log("imageOfAzureLength", imageArrayOfAzure.length);
        }
    }

    useEffect(() => {
        getFile();

        /**
            * uncomment below  code Enable Google Drive integration
        */
        // initClient();
        // checkGoogleFiles();
    }, []);

    return (
        <div style={{ position: "relative" }}>
            <Typography style={{ marginBottom: "8px" }}>Upload billede af patient</Typography>

            {/* uploaded Files UI */}
            {files.length > 0 ? (
                <ImageThumb
                    files={files}
                    multi={multi}
                    maxFiles={maxFiles}
                    journalId={journal?.id}
                    getRootProps={getRootProps}
                    getInputProps={getInputProps}
                    handleEmptyFile={clearFiles}
                    handleDivClick={handleDivClick}
                    handleChangeImage={handleChangeImage}
                    fileInputRef={fileInputRef}
                    handleFileChange={handleFileChange}
                    dragging={dragging}
                    openCapture={() => setOpenCaptureDialog(true)}
                />
            ) : (
                <div className="upload_image_section">
                    {/* Drag Drop & File Select UI */}
                    <label>
                        <div
                            {...getRootProps()}
                            className="upload_image_drag_select"
                            style={{
                                border: dragging && "1px dashed blue",
                                ...(dragging && { zIndex: 1210, backgroundColor: "white", opacity: 1 })
                            }}>
                            <input {...getInputProps()} />

                            <img src="images/docIcon.svg" alt="success" height={40} width={40} />
                            <Typography variant="subtitle1" style={{ marginTop: "10px", fontWeight: "500" }}>
                                Vælg billede til upload
                            </Typography>
                            <Typography variant="body2">Klik her eller træk og slip for at uploade</Typography>
                        </div>
                    </label>

                    {/* divider like or */}
                    <Typography
                        variant="subtitle1"
                        align="center"
                        style={{ margin: "10px", opacity: 0.6, fontWeight: "500" }}>
                        Eller
                    </Typography>

                    {/* Camera Access for upload Image */}
                    <BrowserView>
                        <div className="upload_image_camera" onClick={() => setOpenCaptureDialog(true)} style={{ cursor: 'pointer' }}>
                            <img src="images/cameraIcon.svg" alt="success" height={40} width={34} />
                            <Typography variant="subtitle1" style={{ marginTop: "10px", fontWeight: "500" }}>
                                Tag et billede
                            </Typography>
                        </div>
                    </BrowserView>
                    <MobileView>
                        <div className="upload_image_camera" onClick={handleDivClick} style={{ cursor: 'pointer' }}>
                            <img src="images/cameraIcon.svg" alt="success" height={40} width={34} />
                            <Typography variant="subtitle1" style={{ marginTop: "10px", fontWeight: "500" }}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    capture="filesystem"
                                    hidden
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                />
                                Tag et billede
                            </Typography>
                        </div>
                    </MobileView>
                    {dragging && <div className="dragging_zone" />}
                </div>
            )}

            {/* Preview Area For Upload Image */}
            {previewFiles.length > 0 && (
                <>
                    <UploadPreview
                        error={error}
                        files={files}
                        editMode={Object.keys(editMode).length > 0}
                        maxFiles={maxFiles}
                        previewFiles={previewFiles}
                        getInputProps={getInputProps}
                        getRootProps={getRootProps}
                        handleUpload={handleUpload}
                        handleDelete={handleDeleteImagePreview}
                        handleCancel={handleCancelPreview}
                        handleError={handleError}
                        handleCloseError={handleCloseError}
                    />
                    <div className="dragging_zone" />
                </>
            )}

            {/* Error Alert Display */}
            {error.errorStatus && previewFiles?.length === 0 && (
                <Alert severity="warning" style={{ marginTop: "5px" }} onClose={() => handleCloseError()}>
                    {error.errorMessage}
                </Alert>
            )}
            {openCaptureDialog && <DialogCaptureImage handleClose={() => setOpenCaptureDialog(false)} captureCameraImage={captureCameraImage} />}
        </div>
    );
};
